






































































import { Component, Prop, Vue } from 'vue-property-decorator'
import { Comment } from '@/api/model/comment'
import { User } from '@/api/model/user'
import { resizedToSizeAttachmentUrl } from '@/helpers/resize'
import { cloneDeep } from 'lodash'
import md5 from 'md5'
import { CommentAPI, MentionAPI } from '@/api/backend-client'
import Tooltip from '@/components/ui/Tooltip.vue'
import IconEdit from '@/components/icons/IconEdit.vue'
import IconTrash from '@/components/icons/IconTrash.vue'
import InputMentionable from '@/components/input/InputMentionable.vue'
import InputCheckbox from '@/components/input/InputCheckbox.vue'
import IconVisibility from '@/components/icons/IconVisibility.vue'
import InputSelect from '@/components/input/InputSelect.vue'
import KvButton from '@/components/input/KvButton.vue'
import { CommentOrigin } from '@/api/types'

@Component({
    components: {
        KvButton,
        InputSelect,
        IconVisibility,
        InputCheckbox,
        InputMentionable,
        IconTrash,
        IconEdit,
        Tooltip,
    },
})
export default class CommentBlock extends Vue {
    @Prop({ required: true }) readonly comment!: Comment
    @Prop({ required: true }) readonly mentionableUsers!: User[]
    initialComment: Comment = null
    informAgain: boolean = false
    editing: boolean = false
    saving: boolean = false

    mounted(): void {
        this.initialComment = cloneDeep(this.comment)
    }

    async editComment(): Promise<void> {
        this.editing = true
    }

    async saveComment(): Promise<void> {
        try {
            this.saving = true
            const comment = await CommentAPI.update(this.comment.id, this.comment)
            this.initialComment = cloneDeep(comment)

            if (this.informAgain === true) {
                await MentionAPI.mention(
                    comment,
                    this.mentionedUsers,
                )
            }

            this.$emit('saved', comment)
            this.editing = false

        } catch (error) {
            this.$notify({
                type: 'error',
                title: 'Foutmelding',
                text: error.response.data['hydra:description'] ?? error.message,
            })

        } finally {
            this.saving = false
        }
    }

    async deleteComment(): Promise<void> {
        if (confirm('Weet je zeker dat je deze notitie wilt verwijderen? Dit kan niet ongedaan worden.')) {
            try {
                this.saving = true
                await CommentAPI.delete(this.comment.id)
                this.$emit('deleted', this.comment)
                this.editing = false

            } catch (error) {
                this.$notify({
                    type: 'error',
                    title: 'Foutmelding',
                    text: error.message,
                })

            } finally {
                this.saving = false
            }
        }
    }

    async resetComment(): Promise<void> {
        this.editing = false
        this.$set(this.comment, 'comment', this.initialComment.comment)
        this.$set(this.comment, 'visibility', this.initialComment.visibility)
    }

    visibilityLabel(visibility: string): string {
        return this.$t(`comment.visibility.${visibility}`, {
            userName: this.$currentUser.name,
            companyName: this.$currentCompany.name,

        }).toString()
    }

    renderComment(comment: string): string {
        this.mentionableUsers.forEach(user => {
            comment = comment.replaceAll(
                `@${user.name}`,
                `<span class="text-pink-500">${user.name}</span>`,
            )
        })

        return comment
    }

    get mentionedUsers(): User[] {
        return this.mentionableUsers
            .filter(user => this.comment.comment.indexOf(`@${user.name}`) >= 0)
    }

    get hasMentionedUsers(): boolean {
        return this.mentionedUsers.length > 0
    }

    get hasUser(): boolean {
        return this.comment.user !== null
    }

    get userName(): string {
        switch (this.comment.origin) {
            case CommentOrigin.VIRTUAL_COLLEAGUE: {
                return this.$currentCompany.virtualColleagueName
            }
            case CommentOrigin.USER: {
                if (!this.comment.user) {
                    return 'KeyVue'
                }

                return this.comment.user.name
            }
            case CommentOrigin.SYSTEM:
            default: {
                return 'KeyVue'
            }
        }
    }

    get userTitle(): string|null {
        switch (this.comment.origin) {
            case CommentOrigin.VIRTUAL_COLLEAGUE: {
                return 'Virtuele Collega'
            }
            case CommentOrigin.USER: {
                return null
            }
            case CommentOrigin.SYSTEM:
            default: {
                return null
            }
        }
    }

    get userAvatar(): string {
        switch (this.comment.origin) {
            case CommentOrigin.VIRTUAL_COLLEAGUE: {
                return require(`@/assets/img/avatar/${this.$currentCompany.virtualColleagueAvatar}.jpg`)
            }
            case CommentOrigin.USER: {
                if (this.comment.user.avatarUrl) {
                    return resizedToSizeAttachmentUrl(this.comment.user.avatarUrl, 10)
                }

                return `https://www.gravatar.com/avatar/${md5(this.comment.user.email)}?d=identicon`
            }
            case CommentOrigin.SYSTEM:
            default: {
                return require('@/assets/img/logo.png')
            }
        }
    }

    get userAvatarClasses(): string[] {
        if (!this.comment) {
            return []
        }

        return [
            'comment-block__avatar',
            `comment-block__avatar--${this.comment.origin}`,
        ]
    }

    get visibilityOptions() {
        return ['personal', 'private', 'public'].map(visibility => ({
            label: this.visibilityLabel(visibility),
            value: visibility,
        }))
    }

    get canEditComment(): boolean {
        if (!this.comment.user) {
            return false
        }

        return this.comment.user.id === this.$currentUser.id
    }

    get canDeleteComment(): boolean {
        return this.canEditComment
    }

    get virtualColleagueEnabled(): boolean {
        return !!this.$currentCompany.virtualColleagueAvatar
    }
}
